/**
 * Typography Components — ROBCO TERMLINK
 *
 * Headings are rendered as terminal output rather than as page furniture:
 * page titles carry a `>` prompt and a blinking block cursor, section titles are
 * bracketed the way a Pip-Boy labels a data category. Every export from the old
 * system is preserved so the case-study pages retheme without edits.
 */

import styled, { keyframes, css } from 'styled-components';
import { typography, spacing } from '../styles/designSystem';

// The cursor never stops blinking — it is the single strongest "live terminal" cue.
const blink = keyframes`
  0%, 49%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const cursor = css`
  &::after {
    content: "\\2588";
    margin-left: ${spacing[200]};
    color: ${({ theme }) => theme.primary};
    animation: ${blink} 1.1s steps(1, end) infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 1;
    }
  }
`;

const prompt = css`
  &::before {
    content: "> ";
    color: ${({ theme }) => theme.primary};
    opacity: 0.8;
  }
`;

// ===== PAGE HEADLINE (H1) =====
// Main page title — reads as a command echoed back by the terminal.
export const PageHeadline = styled.h1`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[700]};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ theme }) => theme.primary};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  text-shadow: 0 0 8px ${({ theme }) => theme.glow};
  margin: 0 0 ${spacing[600]};
  line-height: ${typography.lineHeight[200]};
  position: relative;
  display: inline-block;

  ${prompt}
  ${cursor}

  @media (max-width: 768px) {
    font-size: ${typography.fontSize[600]};
  }
`;

// ===== SECTION HEADLINE (H2) =====
// Major section titles — bracketed, the way a Pip-Boy labels a data category.
export const SectionHeadline = styled.h2`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[500]};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ theme }) => theme.primary};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wider};
  text-shadow: 0 0 8px ${({ theme }) => theme.glow};
  margin: 0 0 ${spacing[600]};
  padding-bottom: ${spacing[200]};
  line-height: ${typography.lineHeight[200]};
  position: relative;
  display: inline-block;
  border-bottom: 1px solid ${({ theme }) => theme.borderMedium};

  &::before {
    content: "[ ";
    opacity: 0.55;
  }

  &::after {
    content: " ]";
    opacity: 0.55;
  }

  @media (max-width: 768px) {
    font-size: ${typography.fontSize[400]};
    letter-spacing: ${typography.letterSpacing.wide};
  }
`;

// ===== SUBSECTION HEADLINE (H3) =====
// Subsection titles with optional accent color border.
export const SubsectionHeadline = styled.h3`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[400]};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ theme }) => theme.text};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  margin: 0 0 ${spacing[400]};
  padding-bottom: ${spacing[200]};
  border-bottom: 1px solid ${({ accentColor, theme }) => accentColor || theme.primary};
  line-height: ${typography.lineHeight[200]};

  &::before {
    content: "// ";
    color: ${({ accentColor, theme }) => accentColor || theme.primary};
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: ${typography.fontSize[300]};
  }
`;

// ===== CARD HEADLINE (H3) =====
// Smaller headline for cards and components.
export const CardHeadline = styled.h3`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[300]};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ theme }) => theme.text};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  margin: 0 0 ${spacing[200]};
  line-height: ${typography.lineHeight[300]};
`;

// ===== CASE STUDY HERO TITLE (H1) =====
export const HeroTitle = styled.h1`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[700]};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ theme }) => theme.primary};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  text-shadow: 0 0 10px ${({ theme }) => theme.glow};
  margin: 0;
  line-height: ${typography.lineHeight[200]};
`;

// ===== PAGE SUBTEXT =====
export const PageSubtext = styled.p`
  font-size: ${typography.fontSize[400]};
  line-height: ${typography.lineHeight[500]};
  color: ${({ theme }) => theme.subtleText};
  margin: 0 auto ${spacing[700]};
`;

// ===== SECTION SUBTEXT =====
export const SectionSubtext = styled.p`
  font-size: ${typography.fontSize[300]};
  line-height: ${typography.lineHeight[500]};
  color: ${({ theme }) => theme.text};
  margin: 0 0 ${spacing[400]};
`;

// ===== HERO SUBTEXT =====
export const HeroSubtext = styled.p`
  color: ${({ theme }) => theme.text};
  font-size: ${typography.fontSize[300]};
  line-height: ${typography.lineHeight[500]};
  margin-bottom: ${spacing[700]};
`;

// ===== CARD SUBTITLE =====
export const CardSubtitle = styled.p`
  font-size: ${typography.fontSize[200]};
  color: ${({ theme }) => theme.subtleText};
  margin: ${spacing[100]} 0 0;
  line-height: ${typography.lineHeight[400]};
`;

// ===== CASE STUDY SUMMARY =====
export const CaseStudySummary = styled.p`
  font-size: ${typography.fontSize[300]};
  line-height: ${typography.lineHeight[500]};
  color: ${({ theme }) => theme.text};
  margin: 0;
  max-width: 800px;
`;

// ===== TERMINAL PRIMITIVES =====

/** Small tracked-out mono label — field names, statuses, record keys. */
export const TerminalLabel = styled.span`
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[150]};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.widest};
  color: ${({ color, theme }) => color || theme.primary};
  opacity: 0.85;
`;

/** A line of terminal output, prefixed with a chevron. */
export const TerminalLine = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize[200]};
  line-height: ${typography.lineHeight[500]};
  color: ${({ theme }) => theme.subtleText};
  margin: 0 0 ${spacing[200]};

  &::before {
    content: "> ";
    color: ${({ theme }) => theme.primary};
    opacity: 0.7;
  }
`;

/** Bordered terminal panel — the box-drawing frame used around content blocks. */
export const TerminalPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.borderMedium};
  background: ${({ theme }) => theme.cardBackground};
  box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.55);
  padding: ${spacing[700]};
  position: relative;

  /* Corner ticks, so the panel reads as drawn by a character grid. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    border: 1px solid ${({ theme }) => theme.primary};
    opacity: 0.5;
  }

  &::before {
    top: -1px;
    left: -1px;
    border-right: 0;
    border-bottom: 0;
  }

  &::after {
    bottom: -1px;
    right: -1px;
    border-left: 0;
    border-top: 0;
  }
`;
